import { useMemo, useState } from 'react';
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  format,
  addMonths,
  subMonths,
  isSameMonth,
  isToday,
  parseISO,
} from 'date-fns';
import { Layout } from '@/components/Layout';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { useAuth } from '@/contexts/AuthContext';
import {
  useAdminCollection,
  createAdminDoc,
  updateAdminDoc,
  deleteAdminDoc,
} from '@/hooks/useAdminCollection';
import { CalendarEvent, CalendarEventType } from '@/types';

const COLLECTION = 'calendarEvents';

const EVENT_TYPES: CalendarEventType[] = ['MEETING', 'DEADLINE', 'FOLLOW_UP', 'REMINDER', 'OTHER'];
const TYPE_LABELS: Record<CalendarEventType, string> = {
  MEETING: 'Meeting',
  DEADLINE: 'Deadline',
  FOLLOW_UP: 'Follow-up',
  REMINDER: 'Reminder',
  OTHER: 'Other',
};
const TYPE_COLORS: Record<CalendarEventType, string> = {
  MEETING: '#6ea8fe',
  DEADLINE: 'var(--danger)',
  FOLLOW_UP: 'var(--accent)',
  REMINDER: '#c58af9',
  OTHER: 'var(--text-2)',
};

interface EventForm {
  title: string;
  date: string;
  time: string;
  type: CalendarEventType;
  notes: string;
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function CalendarContent() {
  const { currentUser } = useAuth();
  const { items: events, loading } = useAdminCollection<CalendarEvent>(COLLECTION, 'date');

  const [cursor, setCursor] = useState(() => startOfMonth(new Date()));
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<CalendarEvent | null>(null);
  const [form, setForm] = useState<EventForm>({ title: '', date: '', time: '', type: 'MEETING', notes: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Group events by 'yyyy-MM-dd' for quick day lookup.
  const eventsByDay = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    for (const ev of events) {
      const arr = map.get(ev.date) || [];
      arr.push(ev);
      map.set(ev.date, arr);
    }
    // Sort each day's events by time.
    for (const arr of map.values()) {
      arr.sort((a, b) => (a.time || '99:99').localeCompare(b.time || '99:99'));
    }
    return map;
  }, [events]);

  const days = useMemo(() => {
    const gridStart = startOfWeek(startOfMonth(cursor));
    const gridEnd = endOfWeek(endOfMonth(cursor));
    return eachDayOfInterval({ start: gridStart, end: gridEnd });
  }, [cursor]);

  const upcoming = useMemo(() => {
    const todayKey = format(new Date(), 'yyyy-MM-dd');
    return events
      .filter((e) => e.date >= todayKey)
      .sort((a, b) => a.date.localeCompare(b.date) || (a.time || '').localeCompare(b.time || ''))
      .slice(0, 6);
  }, [events]);

  const openAdd = (dayKey?: string) => {
    setEditing(null);
    setForm({ title: '', date: dayKey || format(new Date(), 'yyyy-MM-dd'), time: '', type: 'MEETING', notes: '' });
    setError('');
    setShowForm(true);
  };

  const openEdit = (ev: CalendarEvent) => {
    setEditing(ev);
    setForm({ title: ev.title, date: ev.date, time: ev.time || '', type: ev.type, notes: ev.notes || '' });
    setError('');
    setShowForm(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) return setError('Title is required');
    if (!form.date) return setError('Date is required');
    setSaving(true);
    setError('');
    try {
      const payload = {
        title: form.title.trim(),
        date: form.date,
        time: form.time || undefined,
        type: form.type,
        notes: form.notes.trim() || undefined,
      };
      if (editing) {
        await updateAdminDoc(COLLECTION, editing.id, payload);
      } else {
        await createAdminDoc(COLLECTION, payload, currentUser!.uid);
      }
      setShowForm(false);
    } catch (err: any) {
      setError(err.message || 'Failed to save event');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (ev: CalendarEvent) => {
    if (!window.confirm(`Delete event "${ev.title}"?`)) return;
    try {
      await deleteAdminDoc(COLLECTION, ev.id);
    } catch (err) {
      console.error('Failed to delete event:', err);
    }
  };

  return (
    <div style={{ padding: '24px 0' }}>
      <div className="terminal-card">
        <div className="card-header">
          <span className="card-title">Calendar</span>
          <div className="card-header-right" style={{ gap: '10px' }}>
            <button className="action-btn secondary crm-mini-btn" onClick={() => setCursor(subMonths(cursor, 1))}>‹ Prev</button>
            <span className="stat-chip">{format(cursor, 'MMMM yyyy')}</span>
            <button className="action-btn secondary crm-mini-btn" onClick={() => setCursor(addMonths(cursor, 1))}>Next ›</button>
            <button className="action-btn secondary crm-mini-btn" onClick={() => setCursor(startOfMonth(new Date()))}>Today</button>
            <button className="action-btn large" onClick={() => openAdd()}>+ Add Event</button>
          </div>
        </div>
        <div className="card-body" style={{ gap: '14px' }}>
          {loading ? (
            <div style={{ padding: '48px', textAlign: 'center', color: 'var(--muted)', fontSize: '11px', letterSpacing: '0.12em' }}>
              Loading…
            </div>
          ) : (
            <div className="cal-grid">
              {WEEKDAYS.map((w) => (
                <div key={w} className="cal-weekday">{w}</div>
              ))}
              {days.map((day) => {
                const key = format(day, 'yyyy-MM-dd');
                const dayEvents = eventsByDay.get(key) || [];
                const muted = !isSameMonth(day, cursor);
                return (
                  <div
                    key={key}
                    className={`cal-cell${muted ? ' cal-cell--muted' : ''}${isToday(day) ? ' cal-cell--today' : ''}`}
                    onClick={() => openAdd(key)}
                  >
                    <span className="cal-daynum">{format(day, 'd')}</span>
                    <div className="cal-events">
                      {dayEvents.map((ev) => (
                        <button
                          key={ev.id}
                          className="cal-event"
                          style={{ borderLeftColor: TYPE_COLORS[ev.type] }}
                          onClick={(e) => { e.stopPropagation(); openEdit(ev); }}
                          title={`${TYPE_LABELS[ev.type]}${ev.time ? ` · ${ev.time}` : ''}`}
                        >
                          {ev.time && <span className="cal-event__time">{ev.time}</span>} {ev.title}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Upcoming list */}
      <div className="terminal-card" style={{ marginTop: '14px' }}>
        <div className="card-header"><span className="card-title">Upcoming</span></div>
        <div className="card-body">
          {upcoming.length === 0 ? (
            <p style={{ color: 'var(--muted)', fontSize: '12px', margin: 0 }}>No upcoming events.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {upcoming.map((ev) => (
                <button key={ev.id} className="cal-upcoming-row" onClick={() => openEdit(ev)}>
                  <span className="cal-upcoming-dot" style={{ background: TYPE_COLORS[ev.type] }} />
                  <span style={{ color: 'var(--text-3)', fontFamily: 'var(--font-mono)', fontSize: '11px', minWidth: '96px' }}>
                    {format(parseISO(ev.date), 'EEE, MMM d')}{ev.time ? ` · ${ev.time}` : ''}
                  </span>
                  <span style={{ color: 'var(--text)', fontSize: '13px' }}>{ev.title}</span>
                  <span style={{ marginLeft: 'auto', color: TYPE_COLORS[ev.type], fontSize: '10px', fontFamily: 'var(--font-mono)', textTransform: 'uppercase' }}>
                    {TYPE_LABELS[ev.type]}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '1rem' }}>
          <div className="terminal-card" style={{ maxWidth: '480px', width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="card-header">
              <span className="card-title">{editing ? 'Edit Event' : 'Add Event'}</span>
            </div>
            <form className="card-body" onSubmit={handleSave}>
              {error && <div className="terminal-alert error">Error: {error}</div>}
              <div className="field-group">
                <label className="field-label">Title *</label>
                <input className="terminal-input" value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Event title" autoFocus />
              </div>
              <div className="crm-form-row">
                <div className="field-group">
                  <label className="field-label">Date *</label>
                  <input className="terminal-input" type="date" value={form.date}
                    onChange={(e) => setForm({ ...form, date: e.target.value })} />
                </div>
                <div className="field-group">
                  <label className="field-label">Time</label>
                  <input className="terminal-input" type="time" value={form.time}
                    onChange={(e) => setForm({ ...form, time: e.target.value })} />
                </div>
              </div>
              <div className="field-group">
                <label className="field-label">Type</label>
                <select className="terminal-select" value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value as CalendarEventType })}>
                  {EVENT_TYPES.map((t) => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
                </select>
              </div>
              <div className="field-group">
                <label className="field-label">Notes</label>
                <textarea className="terminal-textarea" value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Details…" />
              </div>
              <div className="btn-row">
                <button type="submit" className="action-btn large" disabled={saving}>
                  {saving ? 'Saving…' : editing ? 'Save Changes' : 'Add Event'}
                </button>
                {editing && (
                  <button type="button" className="action-btn danger" onClick={() => handleDelete(editing)} disabled={saving}>
                    Delete
                  </button>
                )}
                <button type="button" className="action-btn secondary" onClick={() => setShowForm(false)} disabled={saving}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export function AdminCalendarPage() {
  return (
    <ProtectedRoute requireAdmin>
      <Layout wide>
        <CalendarContent />
      </Layout>
    </ProtectedRoute>
  );
}
